import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import DocumentTemplateCard from "./DocumentTemplateCard/DocumentTemplateCard";
import { State } from "./interfaces";
import { CardsContainer, Container } from "./styles";
import useAuthorization from "../../custom-hooks/useAuthorization/useAuthorization";
import useSearchOptions from "../../custom-hooks/useSearchOptions/useSearchOptions";
import { DocumentTemplateFilter } from "../../models/document-templates";

import { NotAuthorizedPage } from "../../reusable-components/NotAuthorizedPage/NotAuthorizedPage";
import CustomTablePagination from "../../reusable-components/Table/CustomTablePagination/CustomTablePagination";
import { DocumentTemplatesApiService } from "../../services/document-templates/document-templates-api.service";
import { AUTHORIZATIONS } from "../../utils/constants/authorization-constants";
import { FT_DT_TEMPLATES } from "../../utils/constants/filter-tables-constants";
import AddEditDocumentTemplate from "../AddEditDocumentTemplate/AddEditDocumentTemplate";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";
import { LegislationsQuickfilter, useLegislationsQuickFilter } from "../Quickfilters/LegislationsQuickfilter";
import QuickfiltersContainer from "../Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import TableHeader from "../TableHeader/TableHeader";

const DocumentTemplatesTable = () => {
  const { t } = useTranslation();
  const tRP = "components.document_templates";

  const hasAccess = useAuthorization([
    AUTHORIZATIONS.CompanyAccountsAccess.LegalEntityAdminAccess,
    AUTHORIZATIONS.LegalEntityAccess.ProcessAccounting,
    AUTHORIZATIONS.LegalEntityAccess.ProcessPayroll,
    AUTHORIZATIONS.SystemAccess.CompanyAccountsConfig,
  ]);

  // const [reload, setReload] = useState<boolean>(false);
  const [state, setState] = useState<State>({
    loading: false,
    items: [],
    error: "",
    page: {
      pageSize: 10,
      pageNumber: 0,
    },
    totalItems: 0,
  });

  const { searchOptions, handleChangeSearchOptions } = useSearchOptions(FT_DT_TEMPLATES);

  const {
    legislationsQuickFilter,
    setLegislationsQuickFilter,
    legislationQFIsReady,
    allLegislations,
    selectedLegislation,
  } = useLegislationsQuickFilter(true);

  //#region load items
  const loadItems = useCallback(
    async (signal?: AbortSignal) => {
      setState((state) => ({ ...state, loading: true }));
      try {
        const response = await new DocumentTemplatesApiService().loadItems(
          {
            legislationIds: [...legislationsQuickFilter.legislations.legislationsIds],
          } as DocumentTemplateFilter,
          searchOptions.query,
          undefined,
          state.page,
          searchOptions.exactMatch,
          60,
          signal
        );
        if (response.error) {
          setState((state) => ({
            ...state,
            error: response.error,
          }));
        } else {
          setState((state) => ({
            ...state,
            items: response.items,
            error: "",
            totalItems: response.totalItems,
          }));
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    },
    [legislationsQuickFilter.legislations.legislationsIds, state.page, searchOptions]
  );
  //#endregion

  //#region change page number
  const handleChangePageNumber = useCallback((pageNumber: number) => {
    setState((state) => ({
      ...state,
      page: {
        ...state.page,
        pageNumber,
      },
    }));
  }, []);
  //#endregion

  //#region change page size
  const handleChangePageSize = useCallback((pageSize: number) => {
    setState((state) => ({
      ...state,
      page: {
        pageNumber: 0,
        pageSize,
      },
    }));
  }, []);
  //#endregion

  useEffect(() => {
    const abortController = new AbortController();

    loadItems(abortController.signal);

    return () => abortController.abort();
  }, [state.page]);

  useEffect(() => {
    if (state.page.pageNumber === 0) {
      const abortController = new AbortController();

      loadItems(abortController.signal);

      return () => abortController.abort();
    } else {
      setState((state) => ({ ...state, page: { ...state.page, pageNumber: 0 } }));
    }
  }, [legislationsQuickFilter.legislations.legislationsIds, searchOptions.exactMatch, searchOptions.query]);

  if (!hasAccess) {
    return <NotAuthorizedPage />;
  }

  return (
    <>
      <TableHeader
        searchOptions={searchOptions}
        handleChangeSearchOptions={handleChangeSearchOptions}
        tableTitle={`${t(`${tRP}.title`)}`}
        buttonToRender={<AddEditDocumentTemplate handleNotifyParent={loadItems} />}
        quickFilters={
          <QuickfiltersContainer>
            <LegislationsQuickfilter
              quickFilters={legislationsQuickFilter}
              handleChangeQuickFilters={setLegislationsQuickFilter}
              allLegislationsOption
              legislations={allLegislations}
            />
          </QuickfiltersContainer>
        }
      />
      <Container>
        <LoadingOverlay
          loading={state.loading}
          error={state.error}
        >
          <CardsContainer>
            {state.items.map((item) => (
              <AddEditDocumentTemplate
                key={item.id}
                item={item}
                handleNotifyParent={loadItems}
              >
                <DocumentTemplateCard
                  documentTemplate={item}
                  handleNotifyParent={loadItems}
                />
              </AddEditDocumentTemplate>
            ))}
          </CardsContainer>
        </LoadingOverlay>

        <CustomTablePagination
          totalItems={state.totalItems}
          pageNumber={state.page.pageNumber}
          pageSize={state.page.pageSize}
          handleChangePageNumber={handleChangePageNumber}
          handleChangePageSize={handleChangePageSize}
        />
      </Container>
    </>
  );
};

export default DocumentTemplatesTable;
